/**
 * Small shared pieces for the Home dashboard (src/home/*).
 *
 * Everything here is theme-aware the way the rest of the app is: neutrals come
 * from the var-backed gray ramp + bg-surface (so dark mode is free), and the
 * only colour a component picks for itself is the creator's brand `primary`,
 * tinted with alpha(). Never a literal grey hex.
 */
import { useId } from "react";

/** `primary` at an alpha — rgba for hex (the usual case), color-mix otherwise. */
export function alpha(color, a) {
  const raw = String(color || "").trim().replace(/^#/, "");
  const hex = /^[0-9a-f]{3}$/i.test(raw) ? raw.split("").map((c) => c + c).join("") : raw;
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    const n = parseInt(hex, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  }
  return `color-mix(in srgb, ${color} ${Math.round(a * 100)}%, transparent)`;
}

export const cardCls =
  "rounded-2xl border border-gray-200 bg-surface shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-16px_rgba(16,24,40,0.12)]";

/** Section heading used above the Home blocks: title, optional subtitle and action. */
export function HomeSectionTitle({ title, subtitle, action, primary, accent = false }) {
  return (
    <div className="mb-3.5 flex items-end justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2.5">
        {accent ? <span className="mt-1 h-5 w-1 shrink-0 rounded-full" style={{ backgroundColor: primary }} /> : null}
        <div className="min-w-0">
          <h3 className="truncate text-[16px] font-bold tracking-tight text-gray-900">{title}</h3>
          {subtitle ? <p className="mt-0.5 text-[13px] text-gray-500">{subtitle}</p> : null}
        </div>
      </div>
      {action || null}
    </div>
  );
}

/** "View all →" style link in the brand colour. */
export function HomeLink({ children, onClick, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex shrink-0 items-center gap-1 rounded-lg px-1.5 py-1 text-[13px] font-semibold transition hover:opacity-80"
      style={{ color: primary }}
    >
      {children}
      <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
      </svg>
    </button>
  );
}

// Stable, pleasant colours for initials avatars — picked by name so a member
// keeps the same colour everywhere on the page.
const AVATAR_TONES = ["#7C3AED", "#2563EB", "#0D9488", "#DB2777", "#EA580C", "#059669", "#4F46E5", "#0891B2"];

export function initialsOf(name) {
  const clean = String(name || "").split("@")[0].trim();
  const parts = clean.split(/[\s._-]+/).filter(Boolean);
  const two = parts.length > 1 ? parts[0][0] + parts[1][0] : clean.slice(0, 2);
  return (two || "?").toUpperCase();
}

export function HomeAvatar({ name, img, size = 36, ring }) {
  const tone = AVATAR_TONES[[...String(name || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(10, size * 0.36),
        backgroundColor: img ? undefined : tone,
        boxShadow: ring || undefined,
      }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" /> : initialsOf(name)}
    </span>
  );
}

/** Circular progress in the brand colour. `pct` 0–100. */
export function ProgressRing({ pct = 0, size = 72, stroke = 7, primary, children }) {
  const id = useId().replace(/:/g, "");
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <span className="relative inline-grid shrink-0 place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <defs>
          <linearGradient id={`ring-${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={primary} />
            <stop offset="1" stopColor={primary} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-gray-100" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#ring-${id})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (c * clamped) / 100}
          style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)" }}
        />
      </svg>
      <span className="absolute inset-0 grid place-items-center">{children}</span>
    </span>
  );
}

/** Thin progress bar with a soft brand glow. */
export function ProgressBar({ pct = 0, primary, className = "h-2" }) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className={`w-full overflow-hidden rounded-full bg-gray-100 ${className}`}>
      <div
        className="h-full rounded-full"
        style={{
          width: `${clamped}%`,
          background: `linear-gradient(90deg, ${primary}, ${alpha(primary, 0.65)})`,
          boxShadow: `0 0 12px ${alpha(primary, 0.45)}`,
          transition: "width 900ms cubic-bezier(.2,.8,.2,1)",
        }}
      />
    </div>
  );
}

/** A quiet, centred empty state inside a card: icon, title, one line, optional action. */
export function HomeEmpty({ icon, title, text, action, primary, compact = false }) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/60 text-center ${compact ? "px-4 py-6" : "px-6 py-10"}`}>
      <span className="mb-3 grid h-11 w-11 place-items-center rounded-2xl" style={{ backgroundColor: alpha(primary, 0.1), color: primary }}>
        {icon}
      </span>
      <p className="text-[14px] font-semibold text-gray-800">{title}</p>
      {text ? <p className="mt-1 max-w-xs text-[12.5px] leading-relaxed text-gray-500">{text}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Solid brand button — text colour follows the creator's button-text setting. */
export function BrandButton({ children, onClick, primary, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[13.5px] font-bold transition hover:brightness-[1.06] active:scale-[.98] ${className}`}
      style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)", boxShadow: `0 10px 22px -12px ${primary}` }}
    >
      {children}
    </button>
  );
}

export const Skeleton = ({ className = "" }) => <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />;

/** Morning / afternoon / evening, on the member's own clock. */
export function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/** 1234 → "1,234". */
export const fmt = (n) => Number(n || 0).toLocaleString("en-US");

export const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${Number(n) === 1 ? one : many}`;
